import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import {
  BookOpenIcon,
  AcademicCapIcon,
  PuzzlePieceIcon,
  CheckCircleIcon,
} from "@heroicons/react/24/outline";
import { CourseData } from "@packages/constant/CourseData";
import { useState } from "react";
import {
  CreateLessonPlannerV2RequestDto,
  LessonPlannerV2ResponseDto,
  CreateLessonPlannerV2RequestDtoCourseType,
  CreateLessonPlannerV2RequestDtoPlanType,
  LearningOutcomeDto,
  LearningMethodsDto,
  AdditionalArrangementsDto,
} from "@packages/api/madlenAPI.schemas";
import axios from "axios";
import { createLessonPlannerV2 } from "@packages/api/madlenAPI";
import { getEnvironment } from "@packages/utils";
import { Text } from "@core/index";
interface LessonPlanSummaryProps {
  formData: {
    courseType: string;
    grade: number;
    learningOutcomes: LearningOutcomeDto[];
    crossProgramComponents?: {
      selected: string[];
      customInput?: string;
    };
    learningMethods: LearningMethodsDto;
    additionalInstructions?: string;
    additionalArrangements: AdditionalArrangementsDto;
    sections: {
      [key: string]: {
        id: string;
        name: string;
        details: string;
        duration: number;
      }[];
    };
  };
  onConfirm: (response: LessonPlannerV2ResponseDto) => void;
  onEdit: () => void;
  planType: "DAILY" | "MAARIF";
}

const LessonPlanSummary: React.FC<LessonPlanSummaryProps> = ({
  formData,
  onConfirm,
  onEdit,
  planType,
}) => {
  // console.log("formData", formData);
  const { t, i18n } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const hasOutcomes =
    planType === "MAARIF"
      ? CourseData[formData.courseType as keyof typeof CourseData]
          ?.hasMaarifOutcomes
      : CourseData[formData.courseType as keyof typeof CourseData]?.hasOutcomes;

  const generateLessonPlan = async () => {
    try {
      // console.log("🚀 Starting lesson plan generation...");
      // console.log("📝 Form Data:", JSON.stringify(formData, null, 2));

      setIsLoading(true);
      setError(null);

      // Validate course type
      if (
        !Object.values(CreateLessonPlannerV2RequestDtoCourseType).includes(
          formData.courseType as CreateLessonPlannerV2RequestDtoCourseType,
        )
      ) {
        throw new Error(`Invalid course type: ${formData.courseType}`);
      }

      // Calculate total duration from segments
      const totalDuration = Object.values(formData.sections).reduce(
        (total, activities) => {
          return (
            total +
            activities.reduce(
              (sum: number, activity: { duration: number }) =>
                sum + activity.duration,
              0,
            )
          );
        },
        0,
      );

      // Ensure all required fields have values
      const learningMethodsSelected = formData.learningMethods.selected?.length
        ? formData.learningMethods.selected
        : ["experimental"]; // Default value if empty
      const learningMethodsTeachingStyle =
        formData.learningMethods.teachingStyle ?? 0;
      const nameDate = new Date().toLocaleDateString(i18n.language, {
        day: "numeric",
        month: "numeric",
        year: "numeric",
      });
      const name =
        formData.grade === -3
          ? t("courseType.MONTH_36_48") + ` - ${nameDate}`
          : formData.grade === -2
            ? t("courseType.MONTH_48_60") + ` - ${nameDate}`
            : t(
                `courseType.${CourseData[
                  formData.courseType as keyof typeof CourseData
                ]?.name(formData.grade ?? 8)}`,
              ) +
              ` - ${t("dailyLessonPlan.grade", { grade: formData.grade })} - ${nameDate}`;

      const requestData = {
        additionalArrangements: formData.additionalArrangements,
        additionalInstructions: formData.additionalInstructions || "",
        classDuration: totalDuration || 40,
        courseType:
          formData.courseType as CreateLessonPlannerV2RequestDtoCourseType,
        environment: getEnvironment(),
        grade: formData.grade,
        keyChallenges:
          formData.crossProgramComponents?.customInput ||
          "No specific challenges",
        learningMethods: {
          selected: learningMethodsSelected,
          teachingStyle: learningMethodsTeachingStyle,
        },
        learningObjectives: formData.learningOutcomes.map(
          (outcome) => outcome.text,
        ),
        learningOutcomes: formData.learningOutcomes,
        name: name,
        planType: planType as CreateLessonPlannerV2RequestDtoPlanType,
      } as CreateLessonPlannerV2RequestDto;

      // Validate request data before sending
      const requiredFields = [
        "courseType",
        "grade",
        "learningObjectives",
        "planType",
      ];

      const missingFields = requiredFields.filter((field) => {
        const value =
          requestData[field as keyof CreateLessonPlannerV2RequestDto];
        return (
          value === undefined ||
          value === null ||
          (Array.isArray(value) && value.length === 0) ||
          (typeof value === "string" && value.trim() === "")
        );
      });

      if (missingFields.length > 0) {
        console.error("❌ Missing required fields:", missingFields);
        throw new Error(`Missing required fields: ${missingFields.join(", ")}`);
      }

      // console.log("📤 API Request Data:", JSON.stringify(requestData, null, 2));

      const response = await createLessonPlannerV2(requestData);

      // console.log("📥 API Response:", JSON.stringify(response, null, 2));
      // console.log("✅ Lesson plan generated successfully!");

      onConfirm(response);
    } catch (err) {
      console.error("❌ Error generating lesson plan:", err);

      if (axios.isAxiosError(err) && err.response) {
        console.error("🔍 API Error Details:", {
          status: err.response.status,
          statusText: err.response.statusText,
          data: err.response.data,
          headers: err.response.headers,
          message: err.response.data?.message,
        });

        // Set a more specific error message based on the error response
        if (err.response.data?.message) {
          setError(
            `${t("errors.lessonPlanGeneration")}: ${err.response.data.message}`,
          );
        } else {
          const status = err.response.status;
          switch (status) {
            case 400:
              setError(t("errors.lessonPlanValidation"));
              break;
            case 401:
              setError(t("errors.unauthorized"));
              break;
            case 403:
              setError(t("errors.forbidden"));
              break;
            case 404:
              setError(t("errors.notFound"));
              break;
            default:
              if (status >= 500) {
                setError(t("errors.serverError"));
              } else {
                setError(t("errors.lessonPlanGeneration"));
              }
          }
        }
      } else {
        setError(t("errors.unexpected"));
      }
    } finally {
      setIsLoading(false);
      // console.log("🏁 Generation process completed");
    }
  };

  const leftColumnSections = [
    {
      id: "basicInfo",
      title: t("dailyLessonPlan.steps.basicInfo"),
      icon: BookOpenIcon,
      content: (
        <div>
          <Text variant={"p"}>
            <Text variant="span" weight="medium">
              {t("dailyLessonPlan.lesson")}:
            </Text>{" "}
            {t(
              `courseType.${CourseData[
                formData.courseType as keyof typeof CourseData
              ]?.name(formData.grade ?? 8)}`,
            )}
          </Text>
          <Text variant={"p"}>
            <Text variant="span" weight="medium">
              {t("dailyLessonPlan.gradeLevel")}:
            </Text>{" "}
            {formData.grade === -3
              ? t("courseType.MONTH_36_48")
              : formData.grade === -2
                ? t("courseType.MONTH_48_60")
                : t("dailyLessonPlan.grade", { grade: formData.grade })}
          </Text>
        </div>
      ),
    },
    {
      id: "outcomes",
      title: t("dailyLessonPlan.steps.outcomes"),
      icon: AcademicCapIcon,
      content: (
        <div>
          <div className="mb-3">
            <h4 className="font-medium text-gray-900 dark:text-white mb-2">
              {hasOutcomes
                ? t("dailyLessonPlan.learningOutcomes")
                : t("dailyLessonPlan.selectedTopics")}
            </h4>
            <ul className="list-disc list-inside text-gray-700 dark:text-gray-300">
              {formData.learningOutcomes.map((outcome) => (
                <li key={outcome.id}>{outcome.text}</li>
              ))}
            </ul>
          </div>
        </div>
      ),
    },
  ];

  const rightColumnSections = [
    {
      id: "learningMethods",
      title: t("dailyLessonPlan.steps.learningMethods"),
      icon: PuzzlePieceIcon,
      content: (
        <div>
          <ul className="list-disc list-inside text-gray-700 dark:text-gray-300">
            {formData.learningMethods.selected?.map((method) => (
              <li key={method}>{t(`learningMethods.${method}.title`)}</li>
            ))}
          </ul>
        </div>
      ),
    },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700">
        {/* Header */}
        <div className="border-b border-gray-200 dark:border-gray-700 py-3 px-4">
          <div className="flex items-center justify-center space-x-3">
            <CheckCircleIcon className="w-8 h-8 text-emerald-500" />
            <div>
              <Text variant={"h2"}>dailyLessonPlan.summary.title</Text>
              <Text variant={"p"} status="secondary">
                dailyLessonPlan.summary.description
              </Text>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Left Column */}
            <div className="space-y-4">
              {leftColumnSections.map((section) => (
                <motion.div
                  key={section.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4"
                >
                  <div className="flex items-center mb-3">
                    <div className="p-1.5 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 mr-2">
                      <section.icon className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                    </div>
                    <h3 className="text-base font-medium text-gray-900 dark:text-white">
                      {section.title}
                    </h3>
                  </div>
                  <div className="text-sm">{section.content}</div>
                </motion.div>
              ))}
            </div>

            {/* Right Column */}
            <div className="space-y-4">
              {rightColumnSections.map((section) => (
                <motion.div
                  key={section.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-gray-50 dark:bg-gray-900/50 rounded-lg p-4"
                >
                  <div className="flex items-center mb-3">
                    <div className="p-1.5 rounded-lg bg-emerald-100 dark:bg-emerald-900/30 mr-2">
                      <section.icon className="w-4 h-4 text-emerald-600 dark:text-emerald-400" />
                    </div>
                    <h3 className="text-base font-medium text-gray-900 dark:text-white">
                      {section.title}
                    </h3>
                  </div>
                  <div className="text-sm">{section.content}</div>
                </motion.div>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 dark:border-gray-700 py-3 px-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <Text variant={"p"} status="secondary">
                dailyLessonPlan.summary.note
              </Text>
              {error && (
                <Text variant={"p"} status="error" size={"xs"}>
                  {error}
                </Text>
              )}
            </div>
            <div className="flex space-x-3">
              <button
                onClick={onEdit}
                disabled={isLoading}
                className="px-3 py-1.5 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {t("common.edit")}
              </button>
              <button
                onClick={generateLessonPlan}
                disabled={isLoading}
                className="px-3 py-1.5 text-sm font-medium text-white bg-emerald-600 border border-transparent rounded-md shadow-sm hover:bg-emerald-700 focus:outline-none focus:ring-2 focus:ring-emerald-500 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {isLoading && (
                  <svg
                    className="animate-spin h-4 w-4 text-white"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                )}
                <span>{t("common.generatePlan")}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LessonPlanSummary;
